/**
 * @fileoverview Sales-hierarchy scope resolution.
 * @description Every role-visibility table across the Sales specs (Dashboard, My Page, User
 * Management, Sales Pipeline, Sales Performance, Sales Team) defines access the same way: Super
 * Admin sees everyone, a Sales Head sees their downline, a Sales Manager sees their reporting
 * Executives plus themselves, and a Sales Executive sees only themselves. Rather than each mock
 * endpoint re-deriving that walk over `reportsTo` (and risking 6 slightly different
 * implementations), this file is the one place that computes "which user ids is this viewer
 * allowed to see", given the unified SalesUserRecord roster. A mock endpoint's GET handler applies
 * this scope by filtering its records' ownerId against the returned id list; a real backend would
 * apply the same resolution at the query layer rather than trusting a client-supplied filter.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { TeamRole } from '@/features/sales/types/sales.types'
import type { SalesUserRecord } from '@/features/sales/types/tenant.types'

/**
 * @description Resolves the full downline of a sales user — themselves plus every user who reports
 * to them, directly or transitively (e.g. a Sales Head's downline includes Managers and every
 * Executive reporting to those Managers, as well as any Executive reporting directly to the Head).
 * @param {string} rootUserId - The id of the user whose downline to resolve.
 * @param {SalesUserRecord[]} roster - The full sales-user roster to search within.
 * @returns {string[]} `rootUserId` plus every id transitively reporting to it.
 */
export function resolveDownlineUserIds(rootUserId: string, roster: SalesUserRecord[]): string[] {
  const downline = [rootUserId]
  const queue = [rootUserId]

  while (queue.length > 0) {
    const managerId = queue.shift()
    const directReports = roster.filter((user) => user.reportsTo === managerId)

    for (const report of directReports) {
      downline.push(report.id)
      queue.push(report.id)
    }
  }

  return downline
}

/**
 * @description Resolves which user ids a viewer's role entitles them to see records for, per every
 * spec's role-visibility table. A `null` return means "no filter" (organisation-wide), used only
 * for Super Admin.
 * @param {string} viewerUserId - The signed-in user's id. Ignored (and may be any value, including
 * one absent from `roster`) when `viewerRole` is 'Super Admin'.
 * @param {string} viewerRole - The signed-in user's active role key.
 * @param {SalesUserRecord[]} roster - The full sales-user roster to resolve a downline against.
 * @returns {string[] | null} The permitted user ids, or null for an unscoped (organisation-wide)
 * view.
 */
export function resolveScopedUserIds(
  viewerUserId: string,
  viewerRole: string,
  roster: SalesUserRecord[],
): string[] | null {
  if (viewerRole === 'Super Admin') {
    return null
  }
  return resolveDownlineUserIds(viewerUserId, roster)
}

/**
 * @description Whether the acting viewer may create, edit, deactivate or reactivate a given
 * target user — User Management's "cannot manage accounts outside their authorised hierarchy"
 * rule. A viewer is never considered able to manage their own account through this page (self
 * role/reporting changes and self-deactivation are out of scope here), and the rest falls out of
 * the same downline walk every other scope check in this app already uses: Super Admin manages
 * everyone else, a Sales Head/Manager manages everyone in their resolved downline except
 * themselves, and a Sales Executive's downline is only themselves — so this naturally resolves to
 * false for an Executive, matching "no administrative access to manage other Sales users" without
 * a role special-case.
 * @param {string} actingUserId - The signed-in administrator's roster id.
 * @param {string} actingRole - The signed-in administrator's active role name.
 * @param {string} targetUserId - The user being created/edited/deactivated/reactivated.
 * @param {SalesUserRecord[]} roster - The full sales-user roster.
 * @returns {boolean} Whether the action is within the acting user's authorised scope.
 */
export function canManageSalesUser(
  actingUserId: string,
  actingRole: string,
  targetUserId: string,
  roster: SalesUserRecord[],
): boolean {
  if (targetUserId === actingUserId) {
    return false
  }
  const scopedIds = resolveScopedUserIds(actingUserId, actingRole, roster)
  return scopedIds === null || scopedIds.includes(targetUserId)
}

/**
 * @description Which roles the acting viewer may assign when creating a new user — "Super Admin
 * can create Sales users across the organisation. Sales Heads and Sales Managers manage users
 * within their permitted downline." A Sales Head's downline includes Managers and Executives, so
 * they may create either; a Manager's downline is only their own Executives.
 * @param {string} actingRole - The signed-in administrator's active role name.
 * @returns {TeamRole[]} The roles selectable in the "Add User" form, in seniority order.
 */
export function creatableRolesFor(actingRole: string): TeamRole[] {
  if (actingRole === 'Super Admin') {
    return ['Sales Head', 'Sales Manager', 'Sales Executive']
  }
  if (actingRole === 'Sales Head') {
    return ['Sales Manager', 'Sales Executive']
  }
  if (actingRole === 'Sales Manager') {
    return ['Sales Executive']
  }
  return []
}

/**
 * @description Resolves the valid "Reporting Manager" choices for a given target role, restricted
 * to the acting viewer's own authorised scope — "Reporting selections must remain within the
 * administrator's authorised scope." Hierarchy Rules: a Sales Manager reports to a Sales Head; a
 * Sales Executive reports to a Sales Manager or directly to a Sales Head (the standard structure
 * plus the explicitly supported Head→Executive shortcut); a Sales Head reports to no one — Super
 * Admin is a real roster entry (see SalesUserRecord's own doc comment) but is never a valid
 * `reportsTo` target for a creatable role, since `targetRole` is never itself `'Super Admin'` and
 * `parentRoles` never includes it either.
 * @param {TeamRole} targetRole - The role being assigned to the user being created/edited.
 * @param {string} actingUserId - The signed-in administrator's roster id.
 * @param {string} actingRole - The signed-in administrator's active role name.
 * @param {SalesUserRecord[]} roster - The full sales-user roster to pick candidates from.
 * @returns {SalesUserRecord[]} The eligible reporting-manager candidates, in roster order.
 */
export function eligibleReportingManagersFor(
  targetRole: TeamRole,
  actingUserId: string,
  actingRole: string,
  roster: SalesUserRecord[],
): SalesUserRecord[] {
  if (targetRole === 'Sales Head') {
    return []
  }
  const parentRoles: TeamRole[] =
    targetRole === 'Sales Manager' ? ['Sales Head'] : ['Sales Manager', 'Sales Head']
  const scopedIds = resolveScopedUserIds(actingUserId, actingRole, roster)
  return roster.filter((candidate) => {
    // `.some(role === ...)` rather than `.includes` since `candidate.role` is now wider than
    // `TeamRole` (it also allows 'Super Admin') — never actually equal to any `parentRoles` entry,
    // so Super Admin is correctly never offered as a reporting-manager choice, same as today.
    if (!parentRoles.some((role) => role === candidate.role)) return false
    return scopedIds === null || scopedIds.includes(candidate.id)
  })
}

/**
 * @description Whether assigning `newReportsTo` as a user's reporting manager would create a
 * circular hierarchy — "Reporting relationships must not create circular hierarchies" — by walking
 * up the candidate manager's own chain and checking whether it ever reaches the user being
 * reassigned. A user reporting to themselves is the simplest case of this and is caught the same
 * way (the walk starts there).
 * @param {string} userId - The user whose `reportsTo` is being changed.
 * @param {string} newReportsTo - The proposed new reporting-manager id.
 * @param {SalesUserRecord[]} roster - The full sales-user roster to walk.
 * @returns {boolean} True if the proposed relationship is circular (and must be rejected).
 */
export function wouldCreateReportingCycle(
  userId: string,
  newReportsTo: string,
  roster: SalesUserRecord[],
): boolean {
  let current: string | null = newReportsTo
  const visited = new Set<string>()

  while (current) {
    if (current === userId) return true
    if (visited.has(current)) return false
    visited.add(current)
    current = roster.find((candidate) => candidate.id === current)?.reportsTo ?? null
  }
  return false
}
